import { type SetStateAction, type Dispatch, type FC } from 'react';

import { type ChainId } from '@human-protocol/sdk';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router';

import { ROUTES } from '@/constants';
import { useIsMobile } from '@/hooks/useBreakpoints';
import useCreateEscrow from '@/hooks/useCreateEscrow';
import { type CampaignFormValues } from '@/types';
import { constructCampaignDetails } from '@/utils';

import { SummaryCard, ErrorView, FinalView, BottomNavigation } from '.';

type Props = {
  chainId: ChainId;
  formValues: CampaignFormValues;
  handleChangeStep: Dispatch<SetStateAction<number>>;
  handleStartOver: () => void;
};

const LaunchStep: FC<Props> = ({
  chainId,
  formValues,
  handleChangeStep,
  handleStartOver,
}) => {
  const {
    data: escrowData,
    mutate: createEscrow,
    reset: resetCreateEscrow,
    isLoading: isCreatingEscrow,
    isSuccess: isEscrowCreated,
    isError,
  } = useCreateEscrow();

  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const handleBackToEdit = () => {
    handleChangeStep((prev) => prev - 1);
  };

  const handleLaunchCampaign = async () => {
    await createEscrow(formValues);
  };

  const handleTryAgainClick = () => {
    resetCreateEscrow();
  };

  const onStartOverClick = () => {
    resetCreateEscrow(); // just a safety-belt in case something is cached
    handleStartOver();
  };

  const onViewCampaignDetailsClick = () => {
    if (!escrowData) return;

    const {
      escrowAddress,
      tokenDecimals,
      exchangeOracleFee,
      recordingOracleFee,
      reputationOracleFee,
    } = escrowData;
    const fees = {
      exchangeOracleFee: exchangeOracleFee,
      recordingOracleFee: recordingOracleFee,
      reputationOracleFee: reputationOracleFee,
    };
    const payload = constructCampaignDetails({
      chainId,
      address: escrowAddress,
      data: formValues,
      tokenDecimals,
      fees,
    });
    const encodedData = btoa(JSON.stringify(payload));
    navigate(`/campaign-details/${escrowAddress}?data=${encodedData}`);
  };

  return (
    <>
      {!isEscrowCreated && (
        <Typography variant="h5" sx={{ color: 'neutral.100', mx: 'auto' }}>
          One final look before you initiate the campaign
        </Typography>
      )}
      <Stack sx={{ width: '100%', mt: 5 }}>
        <Paper
          elevation={0}
          sx={{
            width: { xs: '100%', md: '500px' },
            mx: 'auto',
            height: 'fit-content',
            gap: 2,
            borderRadius: '8px',
            border: '1px solid',
            borderColor: 'border.strong',
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          {isEscrowCreated && (
            <FinalView
              campaignType={formValues.type}
              onViewDetails={onViewCampaignDetailsClick}
              handleStartOver={onStartOverClick}
            />
          )}
          {isError && <ErrorView onRetry={handleTryAgainClick} />}
          {isCreatingEscrow && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                width: '100%',
                height: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                background: 'rgba(0, 0, 0, 0.5)',
                zIndex: 1,
                backdropFilter: 'blur(1px)',
              }}
            >
              <CircularProgress size={80} />
            </Box>
          )}
          {!isError && !isEscrowCreated && (
            <>
              <SummaryCard step={5} chainId={chainId} formValues={formValues} />
              {!isMobile && (
                <Stack
                  direction="row"
                  sx={{
                    justifyContent: 'space-between',
                    mt: 0,
                    py: 1.5,
                    px: 2,
                    gap: 1.5,
                    bgcolor: 'background.subtle',
                  }}
                >
                  <Button
                    size="large"
                    variant="outlined"
                    fullWidth
                    disabled={isCreatingEscrow}
                    sx={{
                      color: 'neutral.100',
                      borderColor: 'border.strong',
                    }}
                    onClick={handleBackToEdit}
                  >
                    Back to Edit
                  </Button>
                  <Button
                    size="large"
                    variant="contained"
                    color="accent"
                    fullWidth
                    disabled={isCreatingEscrow}
                    onClick={handleLaunchCampaign}
                  >
                    Go Live
                  </Button>
                </Stack>
              )}
            </>
          )}
        </Paper>
      </Stack>
      {isMobile && !isEscrowCreated && (
        <BottomNavigation
          handleBackClick={handleBackToEdit}
          handleNextClick={isError ? handleTryAgainClick : handleLaunchCampaign}
          disableBackButton={isCreatingEscrow}
          disableNextButton={isCreatingEscrow}
          nextButtonText={isError ? 'Try again' : 'Go Live'}
        />
      )}
      {isMobile && isEscrowCreated && (
        <BottomNavigation
          handleNextClick={() => navigate(ROUTES.DASHBOARD)}
          nextButtonText="Back to Dashboard"
        />
      )}
    </>
  );
};

export default LaunchStep;
